import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase/client/supabaseClient';
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from 'react-hot-toast';

const ShopsAnalyticsPage: React.FC = () => {
  const [shopsAnalytics, setShopsAnalytics] = useState<any[]>([]);
  const [payoutRequests, setPayoutRequests] = useState<any[]>([]);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [processingPayout, setProcessingPayout] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('shopsAnalytics');

  // Fetch shops analytics
const fetchShopsAnalytics = async () => {
    setPayoutLoading(true);
    try {
        // Fetch shops earnings data
        const { data: earningsData, error: earningsError } = await supabase
            .from('shop_earnings')
            .select('shop_id, payouts');
        
        if (earningsError) throw earningsError;
        
        // Get all unique shop IDs
        const shopIds = earningsData.map((shop: any) => shop.shop_id);
        
        // Fetch shop names
        const { data: shopsData, error: shopsError } = await supabase
            .from('print_shops')
            .select('id, name')
            .in('id', shopIds);
            
        if (shopsError) throw shopsError;
        
        // Create a map of shop IDs to names
        const shopNameMap = shopsData.reduce((map: any, shop: any) => {
            map[shop.id] = shop.name;
            return map;
        }, {});

        const analytics = (earningsData || []).map((shop: any) => {
            const payouts = shop.payouts || [];
            const payout_requested = payouts
                .filter((p: any) => p.status === 'requested' || p.status === 'processing')
                .reduce((sum: number, p: any) => sum + Number(p.amount), 0);
            const payouts_given = payouts
                .filter((p: any) => p.status === 'paid')
                .reduce((sum: number, p: any) => sum + Number(p.amount), 0);

            return {
                shop_id: shop.shop_id,
                shop_name: shopNameMap[shop.shop_id] || 'Unknown Shop',
                earnings_today: shop.earnings_today || 0,
                payout_requested,
                payouts_given,
            };
        });

        setShopsAnalytics(analytics);
    } catch (err) {
        toast.error('Failed to load shops analytics');
    } finally {
        setPayoutLoading(false);
    }
};

// Fetch payout requests
const fetchPayoutRequests = async () => {
    setPayoutLoading(true);
    try {
        // Fetch shops earnings data
        const { data: earningsData, error: earningsError } = await supabase
            .from('shop_earnings')
            .select('shop_id, payouts');
        
        if (earningsError) throw earningsError;
        
        // Get all unique shop IDs
        const shopIds = earningsData.map((shop: any) => shop.shop_id);
        
        // Fetch shop names
        const { data: shopsData, error: shopsError } = await supabase
            .from('print_shops')
            .select('id, name')
            .in('id', shopIds);
            
        if (shopsError) throw shopsError;
        
        // Create a map of shop IDs to names
        const shopNameMap = shopsData.reduce((map: any, shop: any) => {
            map[shop.id] = shop.name;
            return map;
        }, {});

        const requests: any[] = [];
        (earningsData || []).forEach((shop: any) => {
            (shop.payouts || []).forEach((p: any) => {
                if (p.status === 'requested' || p.status === 'processing') {
                    requests.push({
                        ...p,
                        shop_id: shop.shop_id,
                        shop_name: shopNameMap[shop.shop_id] || 'Unknown Shop',
                    });
                }
            });
        });

        setPayoutRequests(requests);
    } catch (err) {
        toast.error('Failed to load payout requests');
    } finally {
        setPayoutLoading(false);
    }
};

  // Handle payout status update
  const handlePayoutStatus = async (shopId: string, reference: string, status: string, amount: number) => {
    setProcessingPayout(reference);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/v1/payout/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shopId, reference, amount, status }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Payout marked as ${status}`);
        fetchPayoutRequests();
        fetchShopsAnalytics();
      } else {
        toast.error(data.message || 'Failed to update payout');
      }
    } catch (err) {
      toast.error('Failed to update payout');
    } finally {
      setProcessingPayout(null);
    }
  };

  useEffect(() => {
    fetchShopsAnalytics();
    fetchPayoutRequests();
  }, []);

  // Tab change handler
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === 'shopsAnalytics') fetchShopsAnalytics();
    if (value === 'payoutRequests') fetchPayoutRequests();
  };

  return (
    <div className="p-4">
      <Tabs defaultValue="shopsAnalytics" value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4 w-full">
          <TabsTrigger value="shopsAnalytics" className="flex-1">Shops Analytics</TabsTrigger>
          <TabsTrigger value="payoutRequests" className="flex-1">Payout Requests</TabsTrigger>
        </TabsList>

        {/* Shops Analytics Tab */}
        <TabsContent value="shopsAnalytics">
          <Card>
            <CardHeader>
              <CardTitle>Shops Analytics</CardTitle>
            </CardHeader>
            <CardContent>
              {payoutLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shop</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Earnings Today</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payout Requested</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payouts Given</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {shopsAnalytics.map((shop) => (
                        <tr key={shop.shop_id}>
                          <td className="px-6 py-4 whitespace-nowrap">{shop.shop_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">₹{shop.earnings_today?.toFixed(2) || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap">₹{shop.payout_requested?.toFixed(2) || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap">₹{shop.payouts_given?.toFixed(2) || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payout Requests Tab */}
        <TabsContent value="payoutRequests">
          <Card>
            <CardHeader>
              <CardTitle>Payout Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {payoutLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shop</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payoutRequests.map((req) => (
                        <tr key={req.reference}>
                          <td className="px-6 py-4 whitespace-nowrap">{req.shop_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">₹{req.amount}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{new Date(req.timestamp).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{req.status}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{req.reference}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Button 
                              size="sm" 
                              disabled={processingPayout === req.reference}
                              onClick={() => handlePayoutStatus(req.shop_id, req.reference, 'paid', req.amount)}
                              className="bg-green-600 hover:bg-green-700 text-white mr-2"
                            >
                              Paid
                            </Button>
                            <Button 
                              size="sm" 
                              disabled={processingPayout === req.reference}
                              onClick={() => handlePayoutStatus(req.shop_id, req.reference, 'error', req.amount)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              Error
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ShopsAnalyticsPage;